'use client';

import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import {
  DefaultFirebaseConfig,
  fetchAndActivateConfig,
  firebaseConfig,
  firebaseConfigDynamicGetters,
} from './index';

export interface FirebaseRemoteConfigState extends Partial<DefaultFirebaseConfig> {
  configLoaded: boolean;
}
// Create a context with an undefined initial value
const FirebaseRemoteConfigContext = createContext<FirebaseRemoteConfigState | undefined>(undefined);

interface Props {
  children: ReactNode;
  appFirebaseConfig?: firebaseConfig;
}

// Provider component to fetch and provide remote config values to the app
export const FirebaseRemoteConfigProvider: React.FC<Props> = ({ children, appFirebaseConfig }) => {
  const [configLoaded, setLoaded] = useState(false);
  // Define a state object using the FirebaseRemoteConfigState interface
  const [state, setState] = useState<Partial<DefaultFirebaseConfig>>();
  // Use the useEffect hook to fetch and activate the remote config when the component is mounted
  useEffect(() => {
    (async () => {
      // Fetch and activate the remote config
      await fetchAndActivateConfig(appFirebaseConfig);
      setState(firebaseConfigDynamicGetters());
      setLoaded(true);
    })();
  }, [appFirebaseConfig]);
  return (
    <FirebaseRemoteConfigContext.Provider value={{ configLoaded, ...state }}>
      {children}
    </FirebaseRemoteConfigContext.Provider>
  );
};

// Custom hook to use the FirebaseRemoteConfigContext
export const useFirebaseRemoteConfig = (): FirebaseRemoteConfigState => {
  const context = useContext(FirebaseRemoteConfigContext);

  if (context === undefined) {
    throw new Error('useFirebaseRemoteConfig must be used within a FirebaseRemoteConfigProvider');
  }

  return context;
};

export function isValidFirebaseConfig(config: firebaseConfig | null | undefined): boolean {
  if (!config) return false;

  const requiredKeys: (keyof firebaseConfig)[] = [
    'apiKey',
    'authDomain',
    'databaseURL',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId',
    'measurementId',
  ];

  return requiredKeys.every(
    key => config[key] !== undefined && config[key] !== null && config[key] !== ''
  );
}

export function getFirebaseConfig(
  appConfig: firebaseConfig | null | undefined,
  fallbackConfig: firebaseConfig
): firebaseConfig {
  return isValidFirebaseConfig(appConfig) ? appConfig! : fallbackConfig;
}
